import { Link, NavLink, Outlet } from "react-router-dom";
import { useRole, ROLES } from "../lib/roleContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { KatlasModeChip, HowKatlasWorksButton } from "./Katlas";
import { Button } from "./ui/button";
import { Shield, Trophy, Flag, FileText, Inbox, ScrollText, Handshake, InboxIcon, Layers } from "lucide-react";

export default function Layout() {
  const { role, setRole } = useRole();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
            <div className="h-9 w-9 rounded-lg grid place-items-center bg-[#131318] relative overflow-hidden">
              <Shield className="h-5 w-5 text-white relative z-10" strokeWidth={2} />
              <div className="absolute inset-x-0 bottom-0 h-0.5 signal-bar" />
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-[15px] tracking-tight">ClubCierge</div>
              <div className="text-[11px] text-muted-foreground">Members club operations.</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            <NavItem to="/" icon={Trophy} label="Talent" testId="nav-talent" end />
            <NavItem to="/sponsors" icon={Handshake} label="Sponsors" testId="nav-sponsors" />
            <NavItem to="/inbox" icon={Inbox} label="Inbox" testId="nav-inbox" />
            <NavItem to="/sessions" icon={Flag} label="Sessions" testId="nav-sessions" />
            <NavItem to="/intake" icon={InboxIcon} label="Report" testId="nav-intake" />
            <NavItem to="/incidents" icon={FileText} label="Incidents" testId="nav-incidents" />
            <NavItem to="/receipts" icon={ScrollText} label="Receipts" testId="nav-receipts" />
          </nav>

          <div className="flex-1" />

          <div className="hidden sm:block text-right leading-tight mr-2">
            <div className="text-[11px] tracking-eyebrow text-muted-foreground">Acting as</div>
            <div className="text-sm font-medium">{role.name}</div>
          </div>
          <Select value={role.id} onValueChange={(v) => setRole(ROLES.find((r) => r.id === v))}>
            <SelectTrigger className="w-[200px] font-mono-ui text-xs" data-testid="role-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`role-option-${r.id}`}>
                  {r.label} — {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-2 flex items-center justify-between gap-3">
          <div className="tracking-eyebrow text-muted-foreground">
            AI recommends · Authorised humans decide · The club keeps the evidence
          </div>
          <div className="flex items-center gap-2">
            <KatlasModeChip />
            <Link to="/katlas-capability" data-testid="katlas-capability-link">
              <Button variant="ghost" size="sm" className="text-xs">
                <Layers className="h-3.5 w-3.5 mr-1" /> KATLAS capability
              </Button>
            </Link>
            <HowKatlasWorksButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <Outlet />
      </main>

      <footer className="border-t border-border mt-16 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>Synthetic demo data · Motorsport members club · ClubCierge</div>
          <div className="font-mono-ui">Start with one club. Scale the trust pattern across many.</div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
          isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        }`
      }
    >
      <Icon className="h-4 w-4" />{label}
    </NavLink>
  );
}
